// src/components/Statistiky.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {apiFetch as authApiFetch, logout as authLogout, API_BASE, getToken} from "../auth/authClient";

const card = { border: "1px solid #333", borderRadius: 12, padding: 12, marginBottom: 12 };
const row = { display: "flex", gap: 12, flexWrap: "wrap" };
const btn = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #444",
  cursor: "pointer",
  fontWeight: 900,
  background: "transparent",
};
const input = { padding: "10px 12px", borderRadius: 10, border: "1px solid #444", outline: "none" };
const select = { ...input, cursor: "pointer" };
const h2 = { margin: "6px 0 10px", fontSize: 18, fontWeight: 900 };
const small = { fontSize: 12, opacity: 0.85 };

function todayYMD() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function firstDayOfThisMonth() {
  const d = new Date();
  d.setDate(1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}-01`;
}
function addDaysYMD(ymd, delta) {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + delta);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function fmt(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0";
  return x.toLocaleString("cs-CZ");
}
function fmtMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0 Kč";
  return `${x.toLocaleString("cs-CZ")} Kč`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function getFilenameFromContentDisposition(cd) {
  const s = String(cd || "");
  const mStar = s.match(/filename\*\=UTF-8''([^;]+)/i);
  if (mStar?.[1]) return decodeURIComponent(mStar[1].trim());
  const m = s.match(/filename\=\"?([^\";]+)\"?/i);
  if (m?.[1]) return m[1].trim();
  return "";
}

// ✅ jednotné API s auto logoutem na 401
async function apiFetchSafe(path, options = {}) {
  try {
    return await authApiFetch(path, options);
  } catch (e) {
    if (e?.status === 401) authLogout();
    throw e;
  }
}

// ✅ export přes blob, protože window.open neposílá token
async function downloadAuthedFile(pathWithQuery, fallbackName) {
  const token =
    getToken()||
    "";

  const url = `${API_BASE}${pathWithQuery.startsWith("/") ? "" : "/"}${pathWithQuery}`;

  const r = await fetch(url, {
    method: "GET",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (r.status === 401) {
    authLogout();
    const txt = await r.text().catch(() => "");
    throw new Error(txt || "401 Unauthorized");
  }
  if (!r.ok) {
    const txt = await r.text().catch(() => "");
    throw new Error(txt || `HTTP ${r.status}`);
  }

  const cd = r.headers.get("content-disposition") || "";
  const filename = getFilenameFromContentDisposition(cd) || fallbackName || "export";
  const blob = await r.blob();
  downloadBlob(blob, filename);
}

function Table({ title, rows, rightLabel = "Počet" }) {
  return (
    <div style={card}>
      <div style={h2}>{title}</div>
      {!rows?.length ? (
        <div style={small}>Žádná data.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", borderBottom: "1px solid #333", padding: 8 }}>Název</th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #333", padding: 8 }}>{rightLabel}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ padding: 8, borderBottom: "1px solid #222" }}>{r.name}</td>
                  <td style={{ padding: 8, borderBottom: "1px solid #222", textAlign: "right" }}>{fmt(r.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Statistiky() {
  const [from, setFrom] = useState(firstDayOfThisMonth());
  const [to, setTo] = useState(todayYMD());

  const [type, setType] = useState("all"); // all|kremace|pohreb
  const [stav, setStav] = useState("all"); // all|hotova|rozpracovana|storno

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const query = useMemo(() => {
    const u = new URLSearchParams();
    if (from) u.set("from", from);
    if (to) u.set("to", to);
    if (type) u.set("type", type);
    if (stav) u.set("stav", stav);
    return u.toString();
  }, [from, to, type, stav]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const j = await apiFetchSafe(`/api/statistiky?${query}`, { method: "GET" });
      if (!j?.ok) throw new Error(j?.error || "Chyba načtení statistik");
      setData(j);
    } catch (e) {
      setErr(e?.message || "Chyba načtení statistik");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const kpi = data?.kpi || {};
  const meta = data?.meta || {};

  const exportJson = async () => {
    try {
      await downloadAuthedFile(`/api/statistiky/export.json?${query}`, "statistiky.json");
    } catch (e) {
      setErr(e?.message || "Export JSON se nepovedl");
    }
  };

  const exportCsv = async () => {
    try {
      await downloadAuthedFile(`/api/statistiky/export.csv?${query}`, "statistiky.csv");
    } catch (e) {
      setErr(e?.message || "Export CSV se nepovedl");
    }
  };

  const exportKnihaCsv = async () => {
    try {
      await downloadAuthedFile(
        `/api/kniha/export.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&type=${encodeURIComponent(
          type
        )}&stav=${encodeURIComponent(stav)}&filterBy=datumUmrti`,
        "kniha-zemrelych.csv"
      );
    } catch (e) {
      setErr(e?.message || "Export knihy se nepovedl");
    }
  };

  const quickThisMonth = () => {
    setFrom(firstDayOfThisMonth());
    setTo(todayYMD());
  };
  const quickLast7 = () => {
    const t = todayYMD();
    setTo(t);
    setFrom(addDaysYMD(t, -6));
  };
  const quickLast30 = () => {
    const t = todayYMD();
    setTo(t);
    setFrom(addDaysYMD(t, -29));
  };

  return (
    <div style={{ padding: 12 }}>
      <div style={card}>
        <div style={{ ...h2, marginBottom: 8 }}>Statistiky</div>

        <div style={{ ...row, alignItems: "center" }}>
          <div>
            <div style={small}>Od</div>
            <input style={input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>

          <div>
            <div style={small}>Do</div>
            <input style={input} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>

          <div>
            <div style={small}>Typ</div>
            <select style={select} value={type} onChange={(e) => setType(e.target.value)}>
              <option value="all">Vše</option>
              <option value="kremace">Kremace</option>
              <option value="pohreb">Pohřeb</option>
            </select>
          </div>

          <div>
            <div style={small}>Stav</div>
            <select style={select} value={stav} onChange={(e) => setStav(e.target.value)}>
              <option value="all">Vše</option>
              <option value="rozpracovana">Rozpracované</option>
              <option value="hotova">Hotové</option>
              <option value="storno">Storno</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <button style={btn} onClick={exportJson} disabled={loading}>
              Export statistik (JSON)
            </button>

            <button style={btn} onClick={exportCsv} disabled={loading}>
              Export statistik (CSV)
            </button>

            <button style={{ ...btn, borderColor: "#6cf" }} onClick={exportKnihaCsv} disabled={loading}>
              📘 Export knihy zemřelých (Excel)
            </button>

            <button style={{ ...btn, borderColor: "#666" }} onClick={quickThisMonth}>
              Tento měsíc
            </button>
            <button style={{ ...btn, borderColor: "#666" }} onClick={quickLast7}>
              Posledních 7 dní
            </button>
            <button style={{ ...btn, borderColor: "#666" }} onClick={quickLast30}>
              Posledních 30 dní
            </button>

            <button style={{ ...btn, borderColor: "#8f8" }} onClick={load} disabled={loading}>
              {loading ? "Načítám..." : "Obnovit"}
            </button>
          </div>
        </div>

        {err ? <div style={{ marginTop: 10, color: "#ff6b6b", fontWeight: 800 }}>{err}</div> : null}

        {meta?.generatedAt ? (
          <div style={{ marginTop: 8, ...small }}>
            Vygenerováno: {new Date(meta.generatedAt).toLocaleString("cs-CZ")} | Zakázky celkem: {fmt(meta.zakazkyAll)} |
            Ve filtru: {fmt(meta.zakazkyFiltered)}
          </div>
        ) : null}

        {meta?.zakazkyDir ? <div style={{ ...small, marginTop: 6 }}>Zdroj zakázek: {meta.zakazkyDir}</div> : null}
      </div>

      {/* KPI */}
      <div style={row}>
        <div style={{ ...card, flex: "1 1 240px" }}>
          <div style={small}>Zakázky celkem</div>
          <div style={{ fontSize: 26, fontWeight: 900 }}>{fmt(kpi.zakazkyTotal)}</div>
          <div style={small}>
            Kremace: {fmt(kpi.zakazkyKremace)} | Pohřeb: {fmt(kpi.zakazkyPohreb)}
          </div>
          <div style={small}>Neznámý typ: {fmt(kpi.zakazkyNeznamyTyp)}</div>
        </div>

        <div style={{ ...card, flex: "1 1 240px" }}>
          <div style={small}>Stavy zakázek</div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>
            Hotové: {fmt(kpi.stavHotova)} / Rozprac.: {fmt(kpi.stavRozpracovana)} / Storno: {fmt(kpi.stavStorno)}
          </div>
          <div style={small}>Tip: filtr „Hotové“ = reálný výkon</div>
        </div>

        <div style={{ ...card, flex: "1 1 240px" }}>
          <div style={small}>Převozy (ze zakázek)</div>
          <div style={{ fontSize: 26, fontWeight: 900 }}>{fmt(kpi.kmCelkem)} km</div>
          <div style={small}>Průměr: {fmt(kpi.prumerKm)} km / zakázku</div>
        </div>

        <div style={{ ...card, flex: "1 1 240px" }}>
          <div style={small}>Tržby (pokud je máš ve službách / souhrnu)</div>
          <div style={{ fontSize: 26, fontWeight: 900 }}>{fmtMoney(kpi.trzbyCelkem)}</div>
          <div style={small}>Průměr: {fmtMoney(kpi.prumerTrzba)} / zakázku</div>
        </div>

        <div style={{ ...card, flex: "1 1 240px" }}>
          <div style={small}>Obřady (kalendář)</div>
          <div style={{ fontSize: 26, fontWeight: 900 }}>{fmt(kpi.obradyTotal)}</div>
          <div style={small}>Jen z data/obrady.json ve zvoleném období</div>
        </div>

        <div style={{ ...card, flex: "1 1 240px" }}>
          <div style={small}>Sklad</div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>
            Položek: {fmt(kpi.skladPolozek)} | Pod minimum: {fmt(kpi.skladPodMinimum)}
          </div>
          <div style={small}>Log záznamů: {fmt(kpi.skladLogZaznamu)}</div>
        </div>
      </div>

      {/* TOP z uložených zakázek */}
      <div style={row}>
        <div style={{ flex: "1 1 420px" }}>
          <Table title="Top místa úmrtí (zakázky)" rows={data?.top?.mistaUmrti || []} />
        </div>

        <div style={{ flex: "1 1 420px" }}>
          <Table title="Top místa obřadů (zakázky)" rows={data?.top?.mistaObradu || []} />
        </div>

        <div style={{ flex: "1 1 420px" }}>
          <Table title="Top vypravitelé (zakázky)" rows={data?.top?.vypravitele || []} />
        </div>
      </div>

      <div style={row}>
        <div style={{ flex: "1 1 420px" }}>
          <Table title="Top služby (zakázky)" rows={data?.top?.sluzby || []} />
        </div>

        <div style={{ flex: "1 1 420px" }}>
          <Table title="Top rakve (zakázky)" rows={data?.top?.rakve || []} />
        </div>
      </div>

      {/* TOP z kalendáře */}
      <div style={row}>
        <div style={{ flex: "1 1 420px" }}>
          <Table title="Obřady podle posádky (kalendář)" rows={data?.top?.obradyByPosadka || []} />
        </div>

        <div style={{ flex: "1 1 420px" }}>
          <Table title="Obřady podle místa (kalendář)" rows={data?.top?.obradyByMisto || []} />
        </div>

        <div style={{ flex: "1 1 420px" }}>
          <Table title="Obřady podle typu (kalendář)" rows={data?.top?.obradyByTyp || []} />
        </div>
      </div>

      {/* Sklad pod minimum */}
      <div style={card}>
        <div style={h2}>Sklad – položky pod minimum</div>
        {!data?.sklad?.podMinimum?.length ? (
          <div style={small}>Super – nic není pod minimem.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #333", padding: 8 }}>Název</th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #333", padding: 8 }}>Kód</th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #333", padding: 8 }}>Typ</th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #333", padding: 8 }}>Stav</th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #333", padding: 8 }}>Max</th>
                </tr>
              </thead>
              <tbody>
                {data?.sklad?.podMinimum?.map((s) => (
                  <tr key={s.id}>
                    <td style={{ padding: 8, borderBottom: "1px solid #222" }}>{s.nazev}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid #222" }}>{s.kod}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid #222" }}>{s.typRakve}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid #222", textAlign: "right" }}>{fmt(s.stavKs)}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid #222", textAlign: "right" }}>{fmt(s.maxKs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
